/**
 * Voice Mate Click-to-Call Widget
 *
 * Provides a simple way to add voice calling to any HTML element.
 * When the element is clicked, a popup modal appears with call controls.
 *
 * Usage:
 *   VoiceMateWidget.init({
 *     selector: '#call-button',
 *     serverUrl: 'https://your-server.com',
 *     botNumber: '1234567890'
 *   });
 */

import { VoiceMateClient } from '../core/VoiceMateClient.js';
import { Logger } from '../utils/Logger.js';

export class VoiceMateWidget {
  constructor(config) {
    this.config = {
      selector: config.selector,
      serverUrl: config.serverUrl,
      botNumber: config.botNumber,
      userNumber: config.userNumber || 'web_user',
      language: config.language || 'en',
      autoConnect: config.autoConnect !== false, // Default true
      logLevel: config.logLevel || 'info',
      theme: config.theme || 'light', // 'light' or 'dark'
      position: config.position || 'center', // 'center', 'bottom-right', etc.
      ...config
    };

    this.logger = new Logger(this.config.logLevel);
    this.client = null;
    this.modal = null;
    this.isCallActive = false;
    this.isMuted = false;
    this.elements = null;

    this.initialize();
  }

  /**
   * Static method to initialize the widget
   */
  static init(config) {
    return new VoiceMateWidget(config);
  }

  /**
   * Initialize the widget
   */
  initialize() {
    // Find target elements
    const targets = document.querySelectorAll(this.config.selector);
    if (targets.length === 0) {
      this.logger.warn(`No elements found for selector: ${this.config.selector}`);
      return;
    }

    // Attach click listeners
    targets.forEach(element => {
      element.addEventListener('click', (e) => {
        e.preventDefault();
        this.openModal();
      });
      element.style.cursor = 'pointer';
    });

    this.logger.info(`VoiceMateWidget initialized on ${targets.length} element(s)`);

    // Create modal HTML (hidden initially)
    this.createModal();
  }

  /**
   * Create the modal popup
   */
  createModal() {
    // Create modal container
    const modal = document.createElement('div');
    modal.id = 'voicemate-widget-modal';
    modal.className = `voicemate-modal ${this.config.theme}`;
    modal.style.display = 'none';

    // Modal HTML structure
    modal.innerHTML = `
      <div class="voicemate-modal-overlay"></div>
      <div class="voicemate-modal-content ${this.config.position}">
        <div class="voicemate-modal-header">
          <h3>Voice Call</h3>
          <button class="voicemate-close-btn" aria-label="Close">&times;</button>
        </div>
        <div class="voicemate-modal-body">
          <div class="voicemate-status">
            <div class="voicemate-status-icon">
              <div class="voicemate-pulse"></div>
            </div>
            <p class="voicemate-status-text">Initializing...</p>
          </div>
          <div class="voicemate-controls">
            <button class="voicemate-btn voicemate-mute-btn" disabled>
              <svg class="voicemate-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
              <span class="voicemate-btn-text">Mute</span>
            </button>
            <button class="voicemate-btn voicemate-end-btn voicemate-btn-danger" disabled>
              <svg class="voicemate-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                <line x1="23" y1="1" x2="1" y2="23"></line>
              </svg>
              <span class="voicemate-btn-text">End Call</span>
            </button>
          </div>
          <div class="voicemate-info">
            <p class="voicemate-bot-number">Bot: ${this.config.botNumber}</p>
          </div>
        </div>
        <div class="voicemate-footer">
          <small>Powered by Voice Mate SDK</small>
        </div>
      </div>
    `;

    // Add styles
    this.addStyles();

    // Append to body
    document.body.appendChild(modal);
    this.modal = modal;

    // Store element references
    this.elements = {
      overlay: modal.querySelector('.voicemate-modal-overlay'),
      closeBtn: modal.querySelector('.voicemate-close-btn'),
      statusText: modal.querySelector('.voicemate-status-text'),
      statusIcon: modal.querySelector('.voicemate-status-icon'),
      muteBtn: modal.querySelector('.voicemate-mute-btn'),
      endBtn: modal.querySelector('.voicemate-end-btn'),
      controls: modal.querySelector('.voicemate-controls')
    };

    // Attach event listeners
    this.attachModalListeners();
  }

  /**
   * Add widget styles
   */
  addStyles() {
    if (document.getElementById('voicemate-widget-styles')) {
      return; // Styles already added
    }

    const style = document.createElement('style');
    style.id = 'voicemate-widget-styles';
    style.textContent = `
      .voicemate-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      }

      .voicemate-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
      }

      .voicemate-modal-content {
        position: absolute;
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        max-width: 400px;
        width: 90%;
        animation: voicemate-slide-up 0.3s ease-out;
      }

      .voicemate-modal-content.center {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .voicemate-modal-content.bottom-right {
        bottom: 20px;
        right: 20px;
        transform: none;
      }

      @keyframes voicemate-slide-up {
        from {
          opacity: 0;
          transform: translate(-50%, -40%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }

      .voicemate-modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .voicemate-modal-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #111827;
      }

      .voicemate-close-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .voicemate-close-btn:hover {
        background: #f3f4f6;
        color: #111827;
      }

      .voicemate-modal-body {
        padding: 32px 24px;
      }

      .voicemate-status {
        text-align: center;
        margin-bottom: 32px;
      }

      .voicemate-status-icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 16px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .voicemate-pulse {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: #10b981;
        position: relative;
      }

      .voicemate-pulse::before,
      .voicemate-pulse::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: inherit;
        opacity: 0.6;
        animation: voicemate-pulse 2s ease-out infinite;
      }

      .voicemate-pulse::after {
        animation-delay: 1s;
      }

      @keyframes voicemate-pulse {
        0% {
          transform: scale(1);
          opacity: 0.6;
        }
        100% {
          transform: scale(1.5);
          opacity: 0;
        }
      }

      .voicemate-status-text {
        font-size: 16px;
        color: #374151;
        margin: 0;
      }

      .voicemate-controls {
        display: flex;
        gap: 12px;
        justify-content: center;
        margin-bottom: 24px;
      }

      .voicemate-btn {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s;
        background: #f3f4f6;
        color: #374151;
      }

      .voicemate-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .voicemate-btn:not(:disabled):hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .voicemate-btn-danger {
        background: #ef4444;
        color: white;
      }

      .voicemate-btn-danger:not(:disabled):hover {
        background: #dc2626;
      }

      .voicemate-mute-btn.muted {
        background: #f59e0b;
        color: white;
      }

      .voicemate-icon {
        width: 18px;
        height: 18px;
      }

      .voicemate-info {
        text-align: center;
        padding-top: 16px;
        border-top: 1px solid #e5e7eb;
      }

      .voicemate-bot-number {
        margin: 0;
        font-size: 12px;
        color: #6b7280;
      }

      .voicemate-footer {
        padding: 12px 24px;
        background: #f9fafb;
        border-top: 1px solid #e5e7eb;
        text-align: center;
        border-radius: 0 0 16px 16px;
      }

      .voicemate-footer small {
        color: #9ca3af;
        font-size: 11px;
      }

      /* Dark theme */
      .voicemate-modal.dark .voicemate-modal-content {
        background: #1f2937;
        color: #f9fafb;
      }

      .voicemate-modal.dark .voicemate-modal-header {
        border-bottom-color: #374151;
      }

      .voicemate-modal.dark .voicemate-modal-header h3 {
        color: #f9fafb;
      }

      .voicemate-modal.dark .voicemate-close-btn {
        color: #9ca3af;
      }

      .voicemate-modal.dark .voicemate-close-btn:hover {
        background: #374151;
        color: #f9fafb;
      }

      .voicemate-modal.dark .voicemate-status-text {
        color: #d1d5db;
      }

      .voicemate-modal.dark .voicemate-btn {
        background: #374151;
        color: #f9fafb;
      }

      .voicemate-modal.dark .voicemate-info {
        border-top-color: #374151;
      }

      .voicemate-modal.dark .voicemate-footer {
        background: #111827;
        border-top-color: #374151;
      }

      /* Responsive */
      @media (max-width: 480px) {
        .voicemate-modal-content {
          width: 95%;
          max-width: none;
        }

        .voicemate-modal-header {
          padding: 16px 20px;
        }

        .voicemate-modal-body {
          padding: 24px 20px;
        }

        .voicemate-controls {
          flex-direction: column;
        }

        .voicemate-btn {
          width: 100%;
          justify-content: center;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Attach event listeners to modal elements
   */
  attachModalListeners() {
    // Close button only - overlay click does NOT close
    this.elements.closeBtn.addEventListener('click', () => {
      this.closeModal();
    });

    // Mute button
    this.elements.muteBtn.addEventListener('click', () => {
      this.toggleMute();
    });

    // End button
    this.elements.endBtn.addEventListener('click', () => {
      this.endCall();
    });

    // Keyboard shortcuts - M for mute only, no Escape to close
    document.addEventListener('keydown', (e) => {
      if (this.modal.style.display === 'block') {
        if (e.key === 'm' || e.key === 'M') {
          this.toggleMute();
        }
      }
    });
  }

  /**
   * Open the modal and start call
   */
  async openModal() {
    this.modal.style.display = 'block';
    this.updateStatus('Connecting...', 'connecting');

    try {
      // ALWAYS create a fresh client instance for clean state
      if (this.client) {
        this.logger.info('Cleaning up existing client...');
        try {
          await this.client.disconnect();
        } catch (err) {
          this.logger.warn('Error disconnecting old client:', err);
        }
        this.client = null;
      }

      this.logger.info('Creating new client instance...');
      this.client = new VoiceMateClient({
        serverUrl: this.config.serverUrl,
        botNumber: this.config.botNumber,
        userNumber: this.config.userNumber,
        language: this.config.language,
        logLevel: this.config.logLevel
      });

      // Setup event listeners
      this.setupClientListeners();

      // Connect and start call
      if (this.config.autoConnect) {
        await this.client.connect();
        await this.client.startCall();
      } else {
        await this.client.connect();
        this.updateStatus('Connected - Ready to call', 'ready');
        this.elements.endBtn.textContent = 'Start Call';
        this.elements.endBtn.disabled = false;
      }

    } catch (error) {
      this.logger.error('Failed to start call:', error);
      this.updateStatus(`Error: ${error.message}`, 'error');
    }
  }

  /**
   * Setup client event listeners
   */
  setupClientListeners() {
    this.client.on('connected', () => {
      this.updateStatus('Connected', 'connected');
    });

    this.client.on('call:started', () => {
      this.isCallActive = true;
      this.updateStatus('Call Active', 'in-call');
      this.elements.muteBtn.disabled = false;
      this.elements.endBtn.disabled = false;
    });

    this.client.on('call:ended', () => {
      this.isCallActive = false;
      this.updateStatus('Call Ended', 'ended');
      this.elements.muteBtn.disabled = true;
      this.elements.endBtn.disabled = true;
    });

    this.client.on('error', (error) => {
      this.updateStatus(`Error: ${error.message}`, 'error');
    });

    this.client.on('disconnected', () => {
      this.updateStatus('Disconnected', 'disconnected');
    });
  }

  /**
   * Update status display
   */
  updateStatus(text, state) {
    this.elements.statusText.textContent = text;

    // Update pulse color based on state
    const pulse = this.elements.statusIcon.querySelector('.voicemate-pulse');
    const colors = {
      'connecting': '#f59e0b',
      'connected': '#10b981',
      'in-call': '#3b82f6',
      'ended': '#6b7280',
      'error': '#ef4444',
      'disconnected': '#6b7280',
      'ready': '#10b981'
    };
    pulse.style.background = colors[state] || '#10b981';
  }

  /**
   * Toggle mute
   */
  toggleMute() {
    if (!this.isCallActive) return;

    if (this.isMuted) {
      this.client.unmute();
      this.isMuted = false;
      this.elements.muteBtn.classList.remove('muted');
      this.elements.muteBtn.querySelector('.voicemate-btn-text').textContent = 'Mute';
    } else {
      this.client.mute();
      this.isMuted = true;
      this.elements.muteBtn.classList.add('muted');
      this.elements.muteBtn.querySelector('.voicemate-btn-text').textContent = 'Unmute';
    }
  }

  /**
   * End call
   */
  async endCall() {
    if (!this.isCallActive) return;

    this.logger.info('Ending call from widget...');

    try {
      // Stop the call (stops audio locally)
      await this.client.endCall();

      // Send control message to server to end call
      if (this.client && this.client.getState().connection === 'connected') {
        this.client.sendControl({
          type: 'end_call',
          reason: 'user_initiated'
        });
      }

      this.isCallActive = false;
      this.updateStatus('Call Ended', 'ended');
      this.elements.muteBtn.disabled = true;
      this.elements.endBtn.disabled = true;

      // Auto-close modal after ending call
      this.closeModal();

    } catch (error) {
      this.logger.error('Failed to end call:', error);
      // Force state reset even on error
      this.isCallActive = false;
      this.elements.muteBtn.disabled = true;
      this.elements.endBtn.disabled = true;
      throw error;
    }
  }

  /**
   * Close modal
   */
  async closeModal() {
    this.logger.info('Closing modal...');

    try {
      // Step 1: End call if active
      if (this.isCallActive) {
        try {
          await this.endCall();
        } catch (error) {
          this.logger.error('Error ending call during close:', error);
          // Continue with cleanup even if endCall fails
        }
      }

      // Step 2: Disconnect and cleanup client
      if (this.client) {
        try {
          await this.client.disconnect();
        } catch (error) {
          this.logger.error('Error disconnecting client:', error);
        }
        // Always nullify client to ensure fresh instance next time
        this.client = null;
      }

    } catch (error) {
      this.logger.error('Error during modal cleanup:', error);
    } finally {
      // Step 3: Always reset UI state (even if errors occurred)
      this.modal.style.display = 'none';
      this.isCallActive = false;
      this.isMuted = false;
      this.elements.muteBtn.disabled = true;
      this.elements.endBtn.disabled = true;
      this.elements.muteBtn.classList.remove('muted');
      this.updateStatus('Initializing...', 'disconnected');
    }
  }

  /**
   * Destroy widget
   */
  destroy() {
    if (this.client) {
      this.client.disconnect();
    }
    if (this.modal) {
      this.modal.remove();
    }
  }
}
